import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Fingerprint, Users, FileText, CheckSquare,
  Megaphone, Building2, MapPin, BarChart3, LogOut, Menu, X, UserCircle, UserCog, CalendarDays,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ROLE_BADGE, HR_WRITE, HR_VIEW_ALL, APPROVERS } from "@/lib/constants";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", roles: "all" },
  { to: "/absensi", label: "Absensi", icon: Fingerprint, testid: "nav-absensi-link", roles: "all" },
  { to: "/formulir", label: "Formulir Saya", icon: FileText, testid: "nav-formulir-link", roles: "all" },
  { to: "/persetujuan", label: "Persetujuan", icon: CheckSquare, testid: "nav-persetujuan-link", roles: APPROVERS },
  { to: "/karyawan", label: "Database Karyawan", icon: Users, testid: "nav-karyawan-link", roles: [...HR_VIEW_ALL, "SUPERVISOR"] },
  { to: "/siklus", label: "Siklus Karyawan", icon: UserCog, testid: "nav-siklus-link", roles: HR_WRITE },
  { to: "/organisasi", label: "Departemen & Jabatan", icon: Building2, testid: "nav-organisasi-link", roles: HR_WRITE },
  { to: "/geofence", label: "Lokasi & Geofence", icon: MapPin, testid: "nav-geofence-link", roles: HR_WRITE },
  { to: "/pengumuman", label: "Pengumuman", icon: Megaphone, testid: "nav-pengumuman-link", roles: "all" },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, testid: "nav-kalender-link", roles: "all" },
  { to: "/laporan", label: "Laporan", icon: BarChart3, testid: "nav-laporan-link", roles: HR_VIEW_ALL },
];

export default function AppLayout() {
  const { user, profile, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const visible = NAV.filter((n) => n.roles === "all" || n.roles.includes(user.role));

  const doLogout = async () => { await logout(); nav("/login"); };

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-6 py-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 text-white">
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <p className="font-heading text-xl font-bold tracking-tight text-white">SHOWTY HRIS</p>
          <p className="text-[10px] text-rose-100/60">Global International</p>
        </div>
      </div>
      <nav className="scrollbar-thin flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {visible.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            end={n.to === "/"}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white/15 text-white"
                  : "text-rose-100/60 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-3">
        <button
          data-testid="logout-button"
          onClick={doLogout}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-rose-950/40 hover:text-rose-300"
        >
          <LogOut className="h-[18px] w-[18px]" /> Keluar
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-950">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 bg-brand-wine lg:block">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-brand-wine">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 dark:border-slate-800 dark:bg-slate-900 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div>
              <p className="text-xs text-slate-500">PT Showty Global International</p>
              <p className="font-heading text-sm font-semibold tracking-tight">Human Resources Information System</p>
            </div>
          </div>
          <button
            data-testid="profile-menu"
            onClick={() => nav("/profil")}
            className="flex items-center gap-3 rounded-full py-1 pl-1 pr-3 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <Avatar className="h-9 w-9">
              <AvatarImage src={profile?.avatar} alt={user.role} />
              <AvatarFallback className="bg-primary/20 text-primary">
                <UserCircle className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            <div className="hidden text-left sm:block">
              <p className="text-sm font-semibold leading-tight">{profile?.full_name || "Pengguna"}</p>
              <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${ROLE_BADGE[user.role]}`}>
                {ROLE_LABELS[user.role]}
              </span>
            </div>
          </button>
        </header>

        <main className="scrollbar-thin flex-1 overflow-y-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
